<template lang="pug" src="./form-editor.pug"></template>
<style lang="scss" src="./form-editor.scss" scoped></style>

<script>
import config from '../../../../config.json';
import { validationMixin } from 'vuelidate';
import { required } from 'vuelidate/lib/validators';
import validate from '@/mixins/validate';
import request from '@/utils/request';
import OptionsDialog from './options-dialog';

export default {
    components: {
        'v-options-dialog': OptionsDialog
    },
    mixins: [validationMixin, validate],
    data: () => ({
        form: {
            name: null,
            description: null
        },
        initialComponents: [],
        component: {
            text: null,
            type: 'simple-question',
            order: 0,
            options: []
        },
        optionsDialog: false,
        components: [],
        loaded: false,
        canSaveForm: false,
        requestProcessing: false
    }),
    validations: {
        form: {
            name: {
                required
            }
        }
    },
    computed: {
        saveMethod() {
            return this.form.id ? 'put' : 'post';
        },
        saveUrl() {
            return this.form.id ? `/forms/${this.form.id}` : '/forms';
        },
        headers() {
            return ['order', 'text', 'type', 'options', 'actions'].map(it => ({
                text: this.$vuetify.lang.t(`$vuetify.pages.Forms.components.${it}`),
                value: it,
                sortable: false,
                align: 'center'
            }));
        },
        questionTypes() {
            return config.forms.questionTypes.map(type => ({
                text: this.$vuetify.lang.t(`$vuetify.pages.Forms.types.${type}`),
                value: type
            }));
        },
        canSaveComponent() {
            const componentFields = Object.reject(this.component, ['order']);
            const allDataFilled = Object.values(componentFields).every(it => !!it);

            if (!!this.component.id && !allDataFilled) {
                return false;
            }

            if (this.canChooseOptions && !this.component.options.length) {
                return false;
            }

            return allDataFilled;
        },
        formIcon() {
            return this.component.id ? 'done_outline' : 'add';
        },
        canChooseOptions() {
            return ['single-choice', 'multiple-choice'].includes(this.component.type);
        },
        optionsTitle() {
            if (!this.component.options.length) {
                return null;
            }

            return this.component.options.join('\n');
        }
    },
    watch: {
        form: {
            handler: function() {
                this.canSaveForm = true;
            },
            deep: true
        }
    },
    created() {
        if (this.$route.params.id) {
            this.loaded = true;
            this.loadData();
        }
    },
    methods: {
        async loadData() {
            const { id: formId } = this.$route.params;

            await request({ method: 'get', url: `/forms/${formId}` })
                .then(res => this.successLoad(res)).catch(error => {
                    if(error.response && error.response.data && error.response.data.error) {
                        this.$store.dispatch('alert/addMessage', {
                            message: error.response.data.error
                        });
                    }
                });

            await request({ method: 'get', url: `forms/${formId}/components` })
                .then(res => {
                    if (res.data) {
                        this.initialComponents = [...res.data];
                        this.components = [...res.data];
                    }
                });
        },

        successLoad(res) {
            this.form = Object.assign({}, this.form, res.data);

            this.$nextTick(() => {
                this.canSaveForm = false;
                this.loaded = false;
            });
        },

        async saveForm() {
            if (this.$v.form.$invalid) {
                this.$v.form.$touch();

                this.$nextTick(() => this.focusFirstErrorInput());
                return;
            }

            if (this.requestProcessing) return;

            this.requestProcessing = true;

            const data = Object.assign({}, this.form);

            await request({ method: this.saveMethod, url: this.saveUrl, data })
                .then(res => {
                    this.successLoad(res);

                    if (!this.$route.params.id) {
                        this.$router.push(`/forms/editor/${this.form.id}`);
                    }

                    this.$store.dispatch('alert/addMessage', {
                        type: 'success',
                        message: 'success'
                    });
                }).catch(
                    error => this.errorHandler(error)
                ).finally(() => {
                    this.$nextTick(() => {
                        this.requestProcessing = false;
                    });
                });
        },

        moveComponent(direction, index) {
            const current = this.component;
            const previous = this.components[index - 1];
            const next = this.components[index + 1];

            // Check direction and swap rows according to it.
            if (direction === 'up' && index > 0) {
                this.$set(current, 'order', index - 1);
                this.$set(this.components, index - 1, current);
                this.$set(this.components, index, previous);
            } else if (direction === 'down' && index < this.components.length - 1) {
                this.$set(current, 'order', index + 1);
                this.$set(this.components, index + 1, current);
                this.$set(this.components, index, next);
            }
        },

        setActiveComponent(component) {
            this.cancelEditing();

            const keys = ['id', ...Object.keys(this.component)];
            const newData = Object.assign({}, {
                ...Object.select(component, keys)
            });

            this.component = JSON.parse(JSON.stringify(newData));
        },

        onSaveOptions(options) {
            this.component.options = [...options];
        },

        async saveComponent() {
            const method = this.component.id ? 'put' : 'post';
            const path = `/forms/${this.form.id}/components`;
            const url = this.component.id ? `${path}/${this.component.id}` : path;

            const order = this.component.order || this.components.length;

            await request({
                method,
                url,
                data: { ...this.component, order }
            }).then(res => {
                if (!this.component.id) {
                    this.components.push(res.data);
                } else {
                    const index = this.components.findIndex(it => it.id == this.component.id);

                    if (index !== -1) {
                        this.$set(this.components, index, res.data);
                    }
                }

                this.initialComponents = [...this.components];

                this.resetComponent();

                this.$store.dispatch('alert/addMessage', {
                    message: 'success',
                    type: 'success'
                });
            }).catch(this.errorHandler);
        },

        async removeComponent(componentId) {
            await request({
                method: 'delete',
                url: `/forms/${this.form.id}/components/${componentId}`
            }).then(res => {
                if (res.status === 204) {
                    const index = this.components.findIndex(it => it.id === componentId);
                    if (index !== -1) {
                        this.components.splice(index, 1);
                        this.initialComponents = [...this.components];
                    }

                    this.$store.dispatch('alert/addMessage', {
                        type: 'success',
                        message: 'success'
                    });
                }
            });
        },

        resetComponent() {
            this.component = {
                text: null,
                type: 'simple-question',
                order: 0,
                options: []
            };
        },

        cancelEditing() {
            this.resetComponent();
            this.components = [...this.initialComponents];
        },

        errorHandler(error) {
            const message = error.response && error.response.data && error.response.data.error;

            if (message) {
                this.$store.dispatch('alert/addMessage', {
                    message: `forms.${message}`
                });
            }
        }
    }
};
</script>
